import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Menu, X, ExternalLink } from 'lucide-react';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [user, setUser] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Check if user is logged in
    const userData = localStorage.getItem('fokatmain_user');
    if (userData) {
      setUser(JSON.parse(userData));
    }
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('fokatmain_token');
    localStorage.removeItem('fokatmain_user');
    localStorage.removeItem('admin_session_token');
    setUser(null);
    window.location.href = '/';
  };

  const handleAdminLogout = () => {
    localStorage.removeItem('admin_session_token');
    window.location.href = '/admin/login';
  };

  // Check if on admin or player dashboard
  const isAdminDashboard = location.pathname.startsWith('/admin');
  const isPlayerDashboard = location.pathname === '/dashboard';
  const isDashboardView = isAdminDashboard || isPlayerDashboard;

  // Different navigation for logged-in vs logged-out users
  const publicNavigation = [
    { name: 'Home', href: '/' },
    { name: 'What is Fokatmain', href: '/what-is-fokatmain' },
    { name: 'Play Games', href: '/play-games' },
    { name: 'Winners', href: '/winners' },
    { name: 'Leaderboard', href: '/leaderboard' },
    { name: 'About', href: '/about' },
    { name: 'For Brands', href: '/brands' },
    { name: 'Careers', href: '/careers' },
    { name: 'Contact', href: '/contact' }
  ];

  const loggedInNavigation = [
    { name: 'Play Games', href: '/play-games' },
    { name: 'Winners', href: '/winners' },
    { name: 'Leaderboard', href: '/leaderboard' },
    { name: 'Dashboard', href: '/dashboard' }
  ];

  // Dashboard-specific navigation (minimal items)
  const dashboardNavigation = [
    { name: 'Home', href: '/' }
  ];

  // Use appropriate navigation based on context
  const navigation = isDashboardView ? dashboardNavigation : (user ? loggedInNavigation : publicNavigation);

  const isActive = (href) => location.pathname === href;

  return (
    <header 
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-black/90 backdrop-blur-md border-b border-gray-800' 
          : 'bg-transparent'
      }`}
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 group">
            <img 
              src="/fokatmain_round_logo.jpeg" 
              alt="Fokatmain Logo" 
              className="h-14 w-14 lg:h-16 lg:w-16 logo-round border-2 border-yellow-400/50 group-hover:border-yellow-400 transition-colors duration-300"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`relative font-medium transition-colors duration-200 hover:text-yellow-400 ${
                  isActive(item.href) 
                    ? 'text-yellow-400' 
                    : 'text-gray-300'
                }`}
              >
                {item.name}
                {isActive(item.href) && (
                  <span className="absolute -bottom-2 left-0 w-full h-0.5 bg-yellow-400 rounded-full" />
                )}
              </Link>
            ))}
          </div>

          {/* Auth Buttons */}
          <div className="hidden lg:flex items-center space-x-4">
            {isAdminDashboard ? (
              <Button 
                onClick={handleAdminLogout}
                className="btn-secondary px-4 py-2 rounded-full font-semibold"
              >
                Admin Logout
              </Button>
            ) : isPlayerDashboard ? (
              <>
                <Link to="/">
                  <Button className="btn-secondary px-4 py-2 rounded-full font-semibold">
                    Home
                  </Button>
                </Link>
                <Button 
                  onClick={handleLogout}
                  className="btn-secondary px-4 py-2 rounded-full font-semibold"
                >
                  Logout
                </Button>
              </>
            ) : user ? (
              <>
                <div className="flex items-center space-x-2 text-gray-300">
                  <span className="text-sm">Hi, {user.name}</span>
                  <span className="text-yellow-400 font-semibold">₹{user.wallet_balance}</span>
                </div>
                <Link to="/dashboard">
                  <Button className="btn-primary px-4 py-2 rounded-full font-semibold">
                    Dashboard
                  </Button>
                </Link>
                <Button 
                  onClick={handleLogout}
                  className="btn-secondary px-4 py-2 rounded-full font-semibold"
                >
                  Logout
                </Button>
              </>
            ) : (
              <>
                <Link to="/login">
                  <Button className="btn-secondary px-4 py-2 rounded-full font-semibold">
                    Login
                  </Button>
                </Link>
                <Link to="/signup">
                  <Button className="btn-primary px-4 py-2 rounded-full font-semibold">
                    Sign Up
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            className="lg:hidden p-2 rounded-md hover:bg-gray-800 transition-colors duration-200"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? (
              <X className="w-6 h-6 text-gray-300" />
            ) : (
              <Menu className="w-6 h-6 text-gray-300" />
            )}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="lg:hidden bg-black/95 backdrop-blur-md border-t border-gray-800 animate-slide-in-left">
            <div className="px-2 pt-2 pb-3 space-y-1">
              {/* Show user info for logged-in users */}
              {user && (
                <div className="px-3 py-2 mb-2 bg-gray-900 rounded-md">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-300">Hi, {user.name}</span>
                    <span className="text-yellow-400 font-semibold">₹{user.wallet_balance || 0}</span>
                  </div>
                </div>
              )}

              {/* Navigation Links */}
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`block px-3 py-2 rounded-md font-medium transition-colors duration-200 ${
                    isActive(item.href)
                      ? 'text-yellow-400 bg-gray-900'
                      : 'text-gray-300 hover:text-yellow-400 hover:bg-gray-800'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}

              {/* Auth Buttons */}
              <div className="pt-4 pb-2 space-y-2 border-t border-gray-800">
                {user ? (
                  <Button 
                    onClick={() => {
                      handleLogout();
                      setIsMenuOpen(false);
                    }}
                    className="w-full btn-secondary py-2 rounded-md font-semibold"
                  >
                    Logout
                  </Button>
                ) : (
                  <>
                    <Link to="/login" onClick={() => setIsMenuOpen(false)}>
                      <Button className="w-full btn-secondary py-2 rounded-md font-semibold">
                        Login
                      </Button>
                    </Link>
                    <Link to="/signup" onClick={() => setIsMenuOpen(false)}>
                      <Button className="w-full btn-primary py-2 rounded-md font-semibold">
                        Sign Up
                      </Button>
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;